import React, { useCallback, useEffect, useMemo, useState } from "react";
import { isEqual } from "lodash";
import { FilterPopup } from "../../../../../../../../Components/FilterPopup/filterPopup.index";
import { UserContext, UserPayload } from "../../../../../../../../Context/userContext";
import { useEscalationInfo } from "../../../../../../../../Context/escalationContext";
import { EscalationFilters } from "../../../../../../../../Models/escalatedPageModels";
import { CUSTOM_VALUE } from "../../../../../../../../constants/dateFormat";
import { useEscalatedDataFetch } from "../../../../../../escalatedPage.hooks";
import { FilterPopupProps } from "../../../../escalatedPageHeader.models";
import {
    buildCreatedByOptions,
    buildEscalationOptions,
    buildPayload,
    buildStatusOptions,
} from "../../../../escalatedPageHeader.utils";
import { getEscalationFilterOptions } from "./escalationFilterPopup.utils";

export const EscalationFilterPopup = ({
    isOpen,
    onClose,
    appliedFilters,
    setAppliedFilters,
}: FilterPopupProps) => {
    const { fetchEscalatedData } = useEscalatedDataFetch();
    const {
        searchValue,
        paginationDetail,
        setPaginationDetail,
        sortInfo,
        escalationList,
        escalationStatusList,
        createdByList,
    } = useEscalationInfo();
    const { userDetail } = React.useContext(UserContext) as UserPayload;

    const [draftFilters, setDraftFilters] = useState<EscalationFilters>(appliedFilters);

    useEffect(() => {
        if (isOpen) setDraftFilters(appliedFilters);
    }, [isOpen, appliedFilters]);

    const updateDraft = useCallback(
        <K extends keyof EscalationFilters>(key: K, value: EscalationFilters[K]) =>
            setDraftFilters((prev) => ({ ...prev, [key]: value })),
        []
    );

    const escalationOptions = useMemo(
        () => buildEscalationOptions(escalationList ?? []),
        [escalationList]
    );

    const escalationStatusOptions = useMemo(
        () => buildStatusOptions(escalationStatusList ?? []),
        [escalationStatusList]
    );

    const createdByOptions = useMemo(
        () => buildCreatedByOptions(createdByList, userDetail.value.devId),
        [createdByList, userDetail.value.devId]
    );

    const filterOptions = useMemo(
        () =>
            getEscalationFilterOptions(
                draftFilters,
                updateDraft,
                createdByOptions,
                escalationStatusOptions,
                escalationOptions
            ),
        [draftFilters, updateDraft, createdByOptions, escalationStatusOptions, escalationOptions]
    );

    const handleApplyClick = useCallback(async () => {
        if (isEqual(draftFilters, appliedFilters)) {
            onClose();
            return;
        }

        const isCustomWithoutDates =
            draftFilters.createdAt === CUSTOM_VALUE &&
            (!draftFilters.customStartDate || !draftFilters.customEndDate);

        const filtersToApply: EscalationFilters = isCustomWithoutDates
            ? {
                  ...draftFilters,
                  createdAt: undefined,
                  customStartDate: undefined,
                  customEndDate: undefined,
              }
            : draftFilters;

        const payload = buildPayload({
            filters: filtersToApply,
            pageSize: paginationDetail.rowsPerPage,
            pageIndex: 1,
            searchParam: searchValue,
            sort: sortInfo,
        });

        setPaginationDetail((prevState) => ({
            ...prevState,
            page: 0,
        }));

        const response = await fetchEscalatedData(payload);
        if (response) {
            setAppliedFilters(filtersToApply);
        }
        onClose();
    }, [
        draftFilters,
        appliedFilters,
        onClose,
        setAppliedFilters,
        fetchEscalatedData,
        paginationDetail.rowsPerPage,
        searchValue,
        sortInfo,
        setPaginationDetail,
    ]);

    return (
        <FilterPopup
            isOpen={isOpen}
            onClose={onClose}
            filterOptions={filterOptions}
            onApply={handleApplyClick}
        />
    );
};
